/**
 * Consistent page header with centered frog mascot and title.
 *
 * On mount, displays the frog mascot large and viewport-centered (page splash),
 * then shrinks and drifts toward the header position before settling into the
 * normal inline layout. Set `splash={false}` to skip the animation.
 *
 * @module PageHeader
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FrogMascot, VARIANT_ALT, VARIANT_FILES, type FrogMascotVariant } from '@/components/FrogMascot';

interface PageHeaderProps {
  /** Which frog image variant to display */
  variant: FrogMascotVariant;
  /** Page title text */
  title: string;
  /** Optional element rendered to the right of the title (e.g. Badge) */
  trailing?: React.ReactNode;
  /** Whether to show the page splash on mount (default true) */
  splash?: boolean;
}

type SplashPhase = 'splash' | 'settling' | 'settled';

const HOLD_MS = 1200;
const SETTLE_MS = 510;

/**
 * Renders a frog mascot inline with the page title in green.
 * Shows a three-phase splash animation on mount unless `splash` is false.
 */
export function PageHeader({ variant, title, trailing, splash = true }: PageHeaderProps) {
  const [phase, setPhase] = useState<SplashPhase>(() => {
    if (!splash) return 'settled';
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'settled';
    return 'splash';
  });
  const [imgLoaded, setImgLoaded] = useState(false);

  const frogRef = useRef<HTMLDivElement>(null);
  const splashRef = useRef<HTMLImageElement>(null);
  const titleSplashRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (phase === 'splash') {
      const t = setTimeout(() => setPhase('settling'), HOLD_MS);
      return () => clearTimeout(t);
    }
    if (phase === 'settling') {
      const t = setTimeout(() => setPhase('settled'), SETTLE_MS);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // FLIP: measure header positions and set CSS variables before paint
  useLayoutEffect(() => {
    if (phase !== 'settling' || !frogRef.current || !splashRef.current) return;

    // Frog FLIP
    const frogTarget = frogRef.current.getBoundingClientRect();
    const frogSrc = splashRef.current.getBoundingClientRect();

    const frogDx = (frogTarget.left + frogTarget.width / 2) - (frogSrc.left + frogSrc.width / 2);
    const frogDy = (frogTarget.top + frogTarget.height / 2) - (frogSrc.top + frogSrc.height / 2);
    const frogScale = frogTarget.width / frogSrc.width;

    splashRef.current.style.setProperty('--settle-x', `${frogDx}px`);
    splashRef.current.style.setProperty('--settle-y', `${frogDy}px`);
    splashRef.current.style.setProperty('--settle-scale', `${frogScale}`);

    // Title FLIP
    if (titleSplashRef.current && titleRef.current) {
      const titleSrc = titleSplashRef.current.getBoundingClientRect();
      const titleTarget = titleRef.current.getBoundingClientRect();

      const titleDx = titleTarget.left - titleSrc.left;
      const titleDy = (titleTarget.top + titleTarget.height / 2) - (titleSrc.top + titleSrc.height / 2);
      const titleScale = titleTarget.height / titleSrc.height;

      titleSplashRef.current.style.setProperty('--settle-x', `${titleDx}px`);
      titleSplashRef.current.style.setProperty('--settle-y', `${titleDy}px`);
      titleSplashRef.current.style.setProperty('--settle-scale', `${titleScale}`);
    }
  }, [phase]);

  return (
    <>
      {/* Fixed overlay for splash + settling phases */}
      {phase !== 'settled' && (
        <div data-testid="page-header-splash-overlay" className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-background">
          <img
            ref={splashRef}
            src={`/images/frog/${VARIANT_FILES[variant]}`}
            alt={VARIANT_ALT[variant]}
            onLoad={() => setImgLoaded(true)}
            className={`w-[480px] h-auto max-w-[85vw] max-h-[70vh] ${
              !imgLoaded ? 'opacity-0' :
              phase === 'splash' ? 'animate-splash-in' : 'animate-frog-settle'
            }`}
          />
          <span
            ref={titleSplashRef}
            style={{ transformOrigin: 'left center' }}
            className={`text-6xl text-primary font-display mt-4 ${
              !imgLoaded ? 'opacity-0' :
              phase === 'splash' ? 'animate-splash-in' : 'animate-title-settle'
            }`}
          >{title}</span>
        </div>
      )}

      {/* Normal inline header — always rendered, visible once overlay disappears */}
      <header className="py-2 flex items-center gap-0.5">
        <div ref={frogRef}>
          <FrogMascot variant={variant} size="xl" />
        </div>
        {phase === 'settled' ? (
          <>
            <h1 ref={titleRef} className="text-4xl text-primary font-display flex-1">{title}</h1>
            {trailing}
          </>
        ) : (
          <h1 ref={titleRef} className="text-4xl text-primary font-display flex-1 opacity-0">{title}</h1>
        )}
      </header>
    </>
  );
}
